import React, { useState } from 'react';
import { 
  Search, Filter, CheckCircle2, Clock, XCircle, 
  FileText, ExternalLink, ShieldCheck, Award, Eye, 
  ChevronRight, ArrowRight, UserCheck 
} from 'lucide-react';
import { INITIAL_APPLICANTS } from '../../data/portalData';

export const ScreeningPortal = ({ 
  setActiveView, 
  onSelectApplicantForOffer,
  applicants: propApplicants,
  onUpdateStatus: propUpdateStatus
}) => {
  const [internalApplicants, setInternalApplicants] = useState(INITIAL_APPLICANTS);
  const applicants = propApplicants || internalApplicants;
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedApplicant, setSelectedApplicant] = useState(applicants[0] || null);

  const handleUpdateStatus = (appNo, newStatus) => {
    if (propUpdateStatus) {
      propUpdateStatus(appNo, newStatus);
    } else {
      setInternalApplicants(prev => prev.map(app => {
        if (app.applicationNo === appNo) {
          return {
            ...app,
            screeningStatus: newStatus,
            admissionLetterIssued: newStatus === 'Approved',
            matricNo: newStatus === 'Approved' ? (app.matricNo || `AKSU/BS/2026/EXEC/${Math.floor(100 + Math.random() * 900)}`) : app.matricNo
          };
        }
        return app;
      }));
    }

    if (selectedApplicant?.applicationNo === appNo) {
      setSelectedApplicant(prev => ({
        ...prev,
        screeningStatus: newStatus,
        admissionLetterIssued: newStatus === 'Approved',
        matricNo: newStatus === 'Approved' ? (prev.matricNo || `AKSU/BS/2026/EXEC/${Math.floor(100 + Math.random() * 900)}`) : prev.matricNo
      }));
    }
  };

  const filtered = filterStatus === 'All'
    ? applicants
    : applicants.filter(a => a.screeningStatus === filterStatus);

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8 space-y-8">
      
      {/* Top Banner in AKSU Emblem Colors */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-2xl p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4 shadow-xl border-2 border-[#7A470E]">
        <div>
          <div className="flex items-center space-x-2 text-xs font-black uppercase tracking-wider text-[#B87A28] font-sc">
            <ShieldCheck className="w-4 h-4" />
            <span>Admissions Board Review Workspace</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-black font-sc mt-1 text-white">
            Executive Admissions Screening Portal
          </h2>
          <p className="text-xs sm:text-sm text-slate-200 mt-1 max-w-xl leading-relaxed">
            Evaluate applicant dossiers, verify Paystack fee settlement, inspect academic transcripts, 
            and recommend candidates for formal Senate admission. <span className="text-amber-300 font-semibold block mt-0.5">Application submission does not guarantee admission until vetted and approved by this board.</span>
          </p>
        </div>

        <div className="flex items-center space-x-3 text-xs">
          <div className="bg-[#030F1C] px-4 py-2.5 rounded-xl border border-slate-800 text-right">
            <span className="text-slate-400 block font-sc">Total In Docket:</span>
            <span className="text-lg font-black text-[#B87A28] font-sc">{applicants.length} Applicants</span>
          </div>
          <button
            onClick={() => setActiveView('apply')}
            className="px-4 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold rounded-xl transition-colors shadow-sm font-sc cursor-pointer"
          >
            + New Online Form
          </button>
        </div>
      </div>

      {/* Main Grid: Left Applicant List, Right Dossier Inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left Column: Applicant Directory */}
        <div className="lg:col-span-5 bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-4 space-y-4">
          
          {/* Filter Pills in AKSU Colors */}
          <div className="flex items-center justify-between gap-1 p-1 bg-slate-100 rounded-xl text-xs font-sc">
            {['All', 'Approved', 'Pending'].map((st) => (
              <button
                key={st}
                onClick={() => setFilterStatus(st)}
                className={`flex-1 py-1.5 font-bold rounded-lg transition-all ${
                  filterStatus === st
                    ? 'bg-[#7A470E] text-white shadow-xs'
                    : 'text-slate-600 hover:text-[#7A470E]'
                }`}
              >
                {st}
              </button>
            ))}
          </div>

          {/* List items */}
          <div className="space-y-2.5 max-h-[600px] overflow-y-auto pr-1">
            {filtered.map((app) => (
              <div
                key={app.applicationNo}
                onClick={() => setSelectedApplicant(app)}
                className={`p-3.5 rounded-xl border-2 transition-all cursor-pointer text-xs ${
                  selectedApplicant?.applicationNo === app.applicationNo
                    ? 'border-[#7A470E] bg-amber-50/40 shadow-sm'
                    : 'border-slate-200 hover:border-[#7A470E] bg-white hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] text-slate-500 font-bold">
                    {app.applicationNo}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-black font-sc ${
                    app.screeningStatus === 'Approved'
                      ? 'bg-emerald-100 text-[#7A470E]'
                      : 'bg-red-100 text-[#0B2644]'
                  }`}>
                    {app.screeningStatus}
                  </span>
                </div>

                <div className="font-black text-slate-900 text-sm mt-1 font-sc">
                  {app.fullName}
                </div>

                <div className="text-slate-600 mt-0.5 line-clamp-1">
                  {app.programmeApplied}
                </div>

                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="flex items-center text-[#7A470E] font-bold">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    ₦25,000 Paid
                  </span>
                  <span className="font-bold text-slate-800">
                    Score: {app.screeningScore}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Deep Dossier Inspector & Decision Console */}
        <div className="lg:col-span-7 bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
          {selectedApplicant ? (
            <>
              {/* Header Profile */}
              <div className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-slate-200 pb-5">
                <div>
                  <span className="text-xs font-mono text-slate-500 block font-bold">
                    {selectedApplicant.applicationNo} · {selectedApplicant.department}
                  </span>
                  <h3 className="text-2xl font-black font-sc text-[#0B2644] mt-0.5">
                    {selectedApplicant.fullName}
                  </h3>
                  <div className="text-xs text-[#7A470E] font-bold mt-1 font-sc">
                    Applying for: {selectedApplicant.programmeApplied}
                  </div>
                </div>

                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-black font-sc ${
                    selectedApplicant.screeningStatus === 'Approved'
                      ? 'bg-emerald-100 text-[#7A470E] border border-emerald-300'
                      : 'bg-red-100 text-[#0B2644] border border-red-300'
                  }`}>
                    Status: {selectedApplicant.screeningStatus}
                  </span>
                  {selectedApplicant.matricNo && (
                    <div className="text-[11px] font-mono font-bold text-[#7A470E] mt-1">
                      Matric: {selectedApplicant.matricNo}
                    </div>
                  )}
                </div>
              </div>

              {/* Verified Paystack Payment Verification Ribbon */}
              <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-300 flex items-center justify-between text-xs text-emerald-950">
                <div className="flex items-center space-x-2">
                  <ShieldCheck className="w-4 h-4 text-[#7A470E]" />
                  <div>
                    <span className="font-bold font-sc">Paystack Transaction Verified: </span>
                    <span className="font-mono font-bold">{selectedApplicant.paystackRef}</span>
                  </div>
                </div>
                <span className="font-black text-[#7A470E]">₦25,000 Cleared</span>
              </div>

              {/* Dossier Information Details */}
              <div className="space-y-4 text-xs">
                <div>
                  <span className="font-black uppercase tracking-wider text-slate-500 block mb-1 font-sc">
                    Academic Qualifications:
                  </span>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-800 font-medium">
                    {selectedApplicant.qualifications}
                  </div>
                </div>

                <div>
                  <span className="font-black uppercase tracking-wider text-slate-500 block mb-1 font-sc">
                    Professional Experience & Corporate Leadership:
                  </span>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-800">
                    {selectedApplicant.workExperience}
                  </div>
                </div>

                <div>
                  <span className="font-black uppercase tracking-wider text-slate-500 block mb-2 font-sc">
                    Submitted Verified Documents:
                  </span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selectedApplicant.documents.map((doc, idx) => (
                      <div 
                        key={idx}
                        className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-between text-[11px]"
                      >
                        <span className="text-slate-700 truncate pr-1">{doc.name}</span>
                        {doc.verified ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#7A470E] shrink-0" title="Verified Document" />
                        ) : (
                          <Clock className="w-3.5 h-3.5 text-[#0B2644] shrink-0" title="Pending Verification" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Admissions Board Decision Buttons */}
              <div className="pt-6 border-t-2 border-slate-200 space-y-3">
                <span className="text-xs font-black uppercase tracking-wider text-slate-700 block font-sc">
                  Board Actions & Senate Recommendation:
                </span>
                
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => handleUpdateStatus(selectedApplicant.applicationNo, 'Approved')}
                    className="px-5 py-2.5 bg-[#0B2644] hover:bg-[#06182B] text-white font-bold text-xs rounded-xl shadow-sm transition-colors flex items-center space-x-1.5 cursor-pointer font-sc"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Approve Full Admission</span>
                  </button>

                  <button
                    onClick={() => handleUpdateStatus(selectedApplicant.applicationNo, 'Conditional')}
                    className="px-5 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold text-xs rounded-xl shadow-sm transition-colors flex items-center space-x-1.5 cursor-pointer font-sc"
                  >
                    <Clock className="w-4 h-4" />
                    <span>Grant Conditional Admission</span>
                  </button>

                  {selectedApplicant.screeningStatus === 'Approved' && (
                    <button
                      onClick={() => {
                        if (onSelectApplicantForOffer) {
                          onSelectApplicantForOffer(selectedApplicant);
                        }
                        setActiveView('offer-letter');
                      }}
                      className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-sm transition-colors flex items-center space-x-1.5 cursor-pointer font-sc"
                    >
                      <FileText className="w-4 h-4 text-[#B87A28]" />
                      <span>View Generated Offer Letter</span>
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-20 text-slate-400 text-xs">
              Select an applicant from the directory to review credentials.
            </div>
          )}
        </div>

      </div>

    </div>
  );
};
